import { useCallback, useEffect, useState } from "react";
import { RefreshCw, CalendarClock, Search, Power, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, fmtErr, fmtDate, fmtDateTime } from "../lib/api";
import { useAuth } from "../lib/auth";
import { RagBadge } from "./Badges";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";

const SwitchOverDialog = ({ open, onClose, onDone }) => {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const go = async () => {
    setBusy(true);
    try {
      const r = await api.post("/asrs/switch-over", { confirm: text });
      toast.success(r.data.final_sync_error ? "Switched over (final sync failed - last good sync kept)" : "Switched over - Quickbase sync stopped");
      onDone();
      onClose();
    } catch (e) { toast.error(fmtErr(e)); } finally { setBusy(false); setText(""); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="border-slate-700 bg-ink-2" data-testid="switch-over-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl uppercase">Switch over from Quickbase</DialogTitle></DialogHeader>
        <ul className="list-disc space-y-1 pl-5 text-sm text-slate-300">
          <li>A final sync runs now, then the hourly Quickbase sync stops.</li>
          <li>All Quickbase history stays in this app.</li>
          <li>Closing a PPM work order rolls its next due date forward by the task frequency (working days).</li>
          <li>Crane engineers should raise all new faults here from now on.</li>
        </ul>
        <input className="field" placeholder="Type SWITCH to confirm" value={text} onChange={(e) => setText(e.target.value)} data-testid="switch-over-confirm-input" />
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose} data-testid="switch-over-cancel">Cancel</Button>
          <Button disabled={text !== "SWITCH" || busy} onClick={go} className="bg-rose-600 hover:bg-rose-700" data-testid="switch-over-confirm">{busy && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}Switch over</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const Stat = ({ label, value, testid, tone = "" }) => (
  <div className="panel p-4" data-testid={testid}><p className={`font-display text-3xl font-extrabold ${tone}`}>{value ?? "-"}</p><p className="eyebrow">{label}</p></div>
);

export const AsrsSummary = () => {
  const { can, user } = useAuth();
  const [s, setS] = useState(null);
  const [switching, setSwitching] = useState(false);
  const load = useCallback(() => api.get("/asrs/summary").then((r) => setS(r.data)), []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    window.addEventListener("wo-changed", load);
    return () => window.removeEventListener("wo-changed", load);
  }, [load]);
  useEffect(() => {
    if (!s?.sync_running) return undefined;
    const t = setTimeout(load, 5000);
    return () => clearTimeout(t);
  }, [s, load]);
  const sync = async () => {
    try {
      await api.post("/asrs/sync");
      toast.success("Quickbase sync started");
      setTimeout(load, 1500);
    } catch (e) { toast.error(fmtErr(e)); }
  };
  if (!s) return null;
  const retired = s.sync?.disabled;
  const resume = async () => {
    if (!window.confirm("Turn the hourly Quickbase sync back on?")) return;
    try {
      await api.post("/asrs/sync-resume");
      toast.success("Quickbase sync resumed");
      load();
    } catch (e) { toast.error(fmtErr(e)); }
  };
  return (
    <div className="mb-6 space-y-3" data-testid="asrs-summary">
      <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
        <Stat label="Plant items" value={s.plants} testid="asrs-stat-plants" />
        <Stat label="Open fault work orders" value={s.open_work_orders} testid="asrs-stat-open-wo" tone="text-amber-400" />
        <Stat label="Open Quickbase faults" value={s.qb_open_faults} testid="asrs-stat-qb-open" />
        <Stat label="Faults last 30 days" value={s.faults_30d} testid="asrs-stat-30d" />
        <Stat label="PPM overdue" value={s.pm_overdue} testid="asrs-stat-pm-overdue" tone={s.pm_overdue ? "text-red-400" : ""} />
        <Stat label="PPM due 14 days" value={s.pm_due_14d} testid="asrs-stat-pm-due" />
      </div>
      <div className="flex flex-wrap items-center gap-3 text-xs text-slate-400" data-testid="asrs-sync-status">
        {retired ? (
          <span className="rounded border border-emerald-500/40 bg-emerald-500/10 px-2 py-1 font-semibold text-emerald-300" data-testid="asrs-qb-retired">Quickbase retired - switched over {fmtDateTime(s.sync.switched_at)} by {s.sync.switched_by}. This app now runs ASRS faults and PPM.</span>
        ) : (
          <span>Quickbase (read-only, hourly): {s.sync_running ? "syncing now..." : s.sync?.last_ok ? `last synced ${fmtDateTime(s.sync.last_ok)}` : "not synced yet"}</span>
        )}
        {s.sync?.error && !retired && <span className="text-red-400" data-testid="asrs-sync-error">Last sync failed: {s.sync.error}</span>}
        {!retired && can("asrs", "manage") && <Button size="sm" variant="outline" disabled={s.sync_running} onClick={sync} className="h-8 border-slate-700 bg-transparent" data-testid="asrs-sync-btn"><RefreshCw className={`mr-1 h-3.5 w-3.5 ${s.sync_running ? "animate-spin" : ""}`} />Sync now</Button>}
        {user?.role === "admin" && !retired && <Button size="sm" onClick={() => setSwitching(true)} className="h-8 bg-rose-600 hover:bg-rose-700" data-testid="asrs-switch-over-btn"><Power className="mr-1 h-3.5 w-3.5" />Switch over to this app</Button>}
        {user?.role === "admin" && retired && <Button size="sm" variant="ghost" onClick={resume} className="h-8" data-testid="asrs-sync-resume-btn">Resume Quickbase sync</Button>}
      </div>
      <SwitchOverDialog open={switching} onClose={() => setSwitching(false)} onDone={load} />
    </div>
  );
};

export const AsrsPm = () => {
  const { can } = useAuth();
  const [rows, setRows] = useState([]);
  const [plant, setPlant] = useState("");
  const load = useCallback(() => api.get("/asrs/pm").then((r) => setRows(r.data)), []);
  useEffect(() => { load(); }, [load]);
  const gen = async () => {
    try {
      const r = await api.post("/asrs/ppm/generate");
      const { created, skipped } = r.data;
      toast.success(created.length ? `Created ${created.length} PPM work order(s)` : skipped ? `All ${skipped} due PPM task(s) already have open work orders` : "No PPM due in the next 14 days");
      window.dispatchEvent(new Event("wo-changed"));
      load();
    } catch (e) { toast.error(fmtErr(e)); }
  };
  const plants = [...new Set(rows.map((r) => r.plant_code))].sort();
  const shown = rows.filter((r) => !plant || r.plant_code === plant);
  return (
    <div className="space-y-3" data-testid="asrs-pm">
      <div className="flex flex-wrap items-center gap-2">
        <select className="field h-10 w-auto" value={plant} onChange={(e) => setPlant(e.target.value)} data-testid="asrs-pm-plant"><option value="">All plant</option>{plants.map((p) => <option key={p}>{p}</option>)}</select>
        <div className="flex-1" />
        {can("asrs", "manage") && <Button variant="outline" className="tap border-slate-700 bg-transparent" onClick={gen} data-testid="asrs-generate-ppm"><CalendarClock className="mr-1 h-4 w-4" />Generate PPM work orders (14 days)</Button>}
      </div>
      <div className="panel overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="border-b border-slate-800 text-left"><tr>{["Plant", "Task", "Due", "Every", "Last done", "Work order", "Status"].map((h) => <th key={h} className="eyebrow px-3 py-3 font-normal">{h}</th>)}</tr></thead>
          <tbody>
            {shown.map((p) => (
              <tr key={p.id} className="border-b border-slate-800/70" data-testid={`asrs-pm-row-${p.qb_id}`}>
                <td className="px-3 py-2 font-mono text-xs text-cyan-300">{p.plant_code}</td>
                <td className="px-3 py-2">{p.task}</td>
                <td className="whitespace-nowrap px-3 py-2">{fmtDate(p.due_date)}</td>
                <td className="whitespace-nowrap px-3 py-2 text-xs text-slate-400">{p.frequency_working_days} working days</td>
                <td className="whitespace-nowrap px-3 py-2 text-xs">{fmtDate(p.app_last_completed || p.last_completed)}</td>
                <td className="px-3 py-2 font-mono text-xs">{p.app_wo || "-"}</td>
                <td className="px-3 py-2"><RagBadge status={p.rag} /></td>
              </tr>
            ))}
            {!shown.length && <tr><td colSpan={7} className="px-3 py-10 text-center text-slate-500">No PPM schedules yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export const AsrsHistory = () => {
  const [d, setD] = useState({ total: 0, items: [] });
  const [f, setF] = useState({ status: "", plant: "", q: "", skip: 0 });
  useEffect(() => {
    const t = setTimeout(() => api.get("/asrs/faults", { params: { ...f, status: f.status || undefined, plant: f.plant || undefined, q: f.q || undefined } }).then((r) => setD(r.data)), 300);
    return () => clearTimeout(t);
  }, [f]);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value, skip: 0 });
  return (
    <div className="space-y-3" data-testid="asrs-history">
      <div className="flex flex-wrap gap-2">
        <div className="relative min-w-[200px] flex-1"><Search className="absolute left-3 top-3 h-4 w-4 text-slate-500" /><input className="field pl-9" placeholder="Search faults, resolutions..." value={f.q} onChange={set("q")} data-testid="asrs-history-search" /></div>
        <input className="field w-32" placeholder="Plant e.g. H08" value={f.plant} onChange={(e) => setF({ ...f, plant: e.target.value.toUpperCase(), skip: 0 })} data-testid="asrs-history-plant" />
        <select className="field w-auto" value={f.status} onChange={set("status")} data-testid="asrs-history-status"><option value="">All</option><option value="open">Open</option><option value="resolved">Resolved</option></select>
      </div>
      <p className="text-xs text-slate-400" data-testid="asrs-history-total">{d.total.toLocaleString()} records from Quickbase</p>
      <div className="panel overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="border-b border-slate-800 text-left"><tr>{["Faulty", "Plant", "Fault", "Location", "Resolution", "Status"].map((h) => <th key={h} className="eyebrow px-3 py-3 font-normal">{h}</th>)}</tr></thead>
          <tbody>
            {d.items.map((r) => (
              <tr key={r.qb_key} className="border-b border-slate-800/70 align-top" data-testid={`asrs-fault-${r.qb_key}`}>
                <td className="whitespace-nowrap px-3 py-2 text-xs">{fmtDateTime(r.faulty_at)}</td>
                <td className="px-3 py-2 font-mono text-xs text-cyan-300">{r.plant_code}</td>
                <td className="px-3 py-2">{r.fault}{r.kind === "corrective" && <span className="ml-1 rounded bg-sky-500/20 px-1 text-[10px] uppercase text-sky-300">Corrective</span>}<p className="text-xs text-slate-400">{r.info}</p></td>
                <td className="px-3 py-2 text-xs">{r.location || "-"}</td>
                <td className="px-3 py-2 text-xs">{[r.resolution, r.resolution_comments, r.corrective].filter(Boolean).join(" - ") || "-"}</td>
                <td className="px-3 py-2"><RagBadge status={r.status === "Resolved" ? "green" : "amber"} label={r.status} /></td>
              </tr>
            ))}
            {!d.items.length && <tr><td colSpan={6} className="px-3 py-10 text-center text-slate-500">No records.</td></tr>}
          </tbody>
        </table>
      </div>
      {d.total > 100 && (
        <div className="flex items-center justify-end gap-2 text-xs">
          <Button size="sm" variant="outline" disabled={!f.skip} onClick={() => setF({ ...f, skip: Math.max(0, f.skip - 100) })} data-testid="asrs-history-prev">Previous</Button>
          <span>{f.skip + 1}-{Math.min(f.skip + 100, d.total)}</span>
          <Button size="sm" variant="outline" disabled={f.skip + 100 >= d.total} onClick={() => setF({ ...f, skip: f.skip + 100 })} data-testid="asrs-history-next">Next</Button>
        </div>
      )}
    </div>
  );
};
